import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone.js";

import HolidaysSvc from "../../../../services/holidaysSvc";
dayjs.extend(timezone);

/**
 * Returns upcoming holidays for the next N days.
 */
export const useUpcomingHolidays = (
  daysForward = 100
): { date: Date; name: string }[] => {
  const currentYear = dayjs().year();

  const { data: thisYearHolidays } = useQuery(["holidays", currentYear], () =>
    HolidaysSvc.getHolidays(currentYear)
  );

  const isDaysForwardInNextYear = !dayjs()
    .add(daysForward, "day")
    .isSame(dayjs(), "year");

  const { data: nextYearHolidays } = useQuery(
    ["holidays", currentYear + 1],
    () => HolidaysSvc.getHolidays(currentYear + 1),
    { enabled: isDaysForwardInNextYear }
  );

  const holidays: Record<string, string> = {
    ...thisYearHolidays,
    ...nextYearHolidays
  };

  return Object.entries(holidays)
    .map(([date, name]) => ({
      date: unixToJapanDate(Number(date)),
      name
    }))
    .filter(
      (holiday) =>
        dayjs(holiday.date).isAfter(dayjs()) &&
        dayjs(holiday.date).isBefore(dayjs().add(daysForward, "day"))
    );
};

// Set to the start of the day in Japan so that users in a different timezone still see the correct date
function unixToJapanDate(unix: number): Date {
  const holidayDate = dayjs.unix(unix).tz("Asia/Tokyo");

  const date = new Date();
  date.setFullYear(holidayDate.year());
  date.setMonth(holidayDate.month());
  date.setDate(holidayDate.date());

  return date;
}
